import dayjs from '../../utils/dayjs';
import { BlockStack, Form, FormLayout, Modal, TextField, Text, SkeletonBodyText } from '@shopify/polaris';
import { ConfettiIcon } from '@shopify/polaris-icons';
import { useField, useForm } from '@shopify/react-form';
import __helpers from 'helpers/index';
import __ from 'languages/index';
import { useGetTransaction, useMutateUpdateTransaction } from 'queries/transactions.query';
import { useCallback, useEffect, useMemo } from 'react';

import { ENUM_ORDER_PAYMENT_STATUS, useMutateGetOrderViaCode } from 'queries/orders.query';
import helpers from 'helpers/index';
import { useNotification } from 'context/NotificationContext';

/** Hiển thị modal để assign đơn hàng vào trong transaction, trong trường hợp người dùng chuyển khoản nhầm nội dung thì vẫn pick được nó ra mà assign, không thể trả lại tiền được  */
export default function TransactionModal({ currentTransaction, show, onClose }: { currentTransaction: string; show: boolean; onClose: () => void }) {
  const { data: entity, isLoading } = useGetTransaction(currentTransaction);
  const { mutateAsync: updateTransactions, isPending: updating } = useMutateUpdateTransaction();
  const { addNotification } = useNotification();
  const { mutateAsync: getOrderData, data: orderData, isPending: loadingOrderViaCode, reset: resetOrderData } = useMutateGetOrderViaCode();

  const useFields = {
    /** create new one */
    order_id: useField<string>({ value: entity?.order_id, validates: [] }),
    order_code: useField<string>({ value: '', validates: [] }),
    description: useField<string>({ value: entity?.description, validates: [] }),
  };

  const { fields, submit, submitting } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        await updateTransactions({
          id: currentTransaction,
          order_id: values.order_id,
          description: values.description,
        });
        addNotification('info', __('Cập nhật thành công!'));
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
        addNotification('error', e.message);
      }
      return { status: 'success', errors: [] };
    },
  });

  const fromClipboard = useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      fields.order_id.onChange(clipboardText);
    } catch (e) {}
  }, []);

  /** Truy vấn tới server */
  const getOrderDataCallback = useCallback(async (order_code) => {
    try {
      await getOrderData(order_code);
    } catch (e) {}
  }, []);

  const debouncedFetch = useMemo(
    () =>
      helpers.debounce((order_code: any) => {
        getOrderDataCallback(order_code); // hoặc bỏ hẳn refetch, gọi API trực tiếp nếu được
      }, 1000),
    []
  );

  useEffect(() => {
    if ((useFields.order_code.value || ' ').length > 2) debouncedFetch(useFields.order_code.value);
  }, [useFields.order_code.value, debouncedFetch]);

  useEffect(() => {
    if (orderData) {
      fields.order_id.onChange(orderData?.id);
    }
  }, [orderData]);

  useEffect(() => {
    if (show) {
      resetOrderData();
    }
  }, [show]);

  return (
    <>
      <Modal
        open={show}
        title={`Hiệu chỉnh một giao dịch`}
        onClose={() => onClose()}
        primaryAction={{
          content: `Cập nhật`,
          icon: ConfettiIcon,
          disabled: isLoading,
          loading: submitting,
          onAction: submit,
        }}
      >
        <Modal.Section>
          <Form onSubmit={submit}>
            <BlockStack gap="400">
              <FormLayout.Group>
                <TextField autoComplete="off" readOnly value={__helpers.formatNumber(entity?.amount)} label={'Số tiền giao dịch'} />
                <TextField autoComplete="off" readOnly value={entity?.payment_method} label={'Loại giao dịch'} />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField autoComplete="off" readOnly value={entity?.casher_data?.display_name || 'Chưa rõ'} label={'Người thu tiền'} />
                <TextField
                  autoComplete="off"
                  readOnly
                  value={dayjs(new Date(Number(entity?.createdAt))).format('HH:mm DD/MM/YYYY')}
                  label={'Ngày ghi nhận'}
                />
              </FormLayout.Group>
              <TextField
                label="Mã đơn hàng"
                autoComplete="off"
                disabled={submitting}
                clearButton
                value={fields.order_code.value}
                labelAction={{
                  content: 'Dán',
                  onAction: () => {
                    fromClipboard();
                  },
                }}
                onChange={(v) => {
                  fields.order_code.onChange(String(v));
                }}
                error={fields.order_code.error}
                onClearButtonClick={() => {
                  fields.order_code.onChange('');
                }}
              />

              <BlockStack gap="400">
                {loadingOrderViaCode ? <SkeletonBodyText /> : null}
                {orderData ? (
                  <>
                    <Text as="p" tone="success">
                      Đơn {orderData?.order_code} của khách hàng {orderData?.customer_data?.display_name} /{' '}
                      {orderData?.customer_data?.user_phonenumber} <br />
                      Đi từ: {orderData?.pickup_address}
                      <br />
                      Điểm đến: {orderData?.dropoff_address}
                      <br />
                      Phải thanh toán:{' '}
                      <Text as="span" fontWeight="bold" variant="headingMd">
                        {helpers.formatNumber(orderData?.total_must_pay)} VND
                      </Text>
                      <br />
                      {orderData?.payment_status === ENUM_ORDER_PAYMENT_STATUS.PAID ? 'ĐÃ THANH TOÁN' : 'CHƯA THANH TOÁN'}
                    </Text>
                  </>
                ) : null}
              </BlockStack>

              <TextField
                autoComplete="off"
                placeholder="Mô tả giao dịch"
                label="Nội dung thanh toán"
                multiline={2}
                disabled={submitting}
                helpText={
                  'Cố gắng không chỉnh sửa nội dung giao dịch gốc khi khách chuyển khoản qua mã QR, vì nó liên quan tới báo cáo thuế. Nếu cần ghi chú, ghi tiếp vào nội dung.'
                }
                {...fields.description}
              />
            </BlockStack>
          </Form>
        </Modal.Section>
      </Modal>
    </>
  );
}

